import { useState, useEffect } from 'react'
import api from '../services/api'

// Cadastro de Planos de Saúde (TPLS001 do ERP): plano em frota.pls001 e validades em frota.pls002

interface PlanoLista {
  plscod: string; plsnum: string; plstip: string; plsemp: string
  fornecedor: string | null; ultima_validade: string; carteiras: number
}

interface Versao {
  plvdtv: string; plvdiv: string
  plvval: string; plvvld: string; plvpfu: string; plvpde: string
  faixas_salario?: number; faixas_tempo?: number; faixas_idade?: number
  nova?: boolean
}

interface Plano { plscod: string; plsnum: string; plstip: string; plsemp: string; fornecedor: string; versoes: Versao[] }

interface Fornecedor { cli_mat: string; cli_nom: string; cli_fan: string }

const br = (iso: string) => (iso && iso > '1900-01-01' ? iso.split('-').reverse().join('/') : '')
const moeda = (v: number) => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const fmtCnpj = (d: string) => {
  const s = (d || '').replace(/\D/g, '').padStart(14, '0')
  return `${s.slice(0, 2)}.${s.slice(2, 5)}.${s.slice(5, 8)}/${s.slice(8, 12)}-${s.slice(12)}`
}
const versaoVazia = (): Versao => ({ plvdtv: '', plvdiv: '', plvval: '0,00', plvvld: '0,00', plvpfu: '0,00', plvpde: '0,00', nova: true })

// Faixas de uma validade — TPLS002 (pls003), TPLS003 (pls004), TPLS004 (pls005)
type GradeKey = 'salario' | 'tempo' | 'idade'
type LinhaFaixa = Record<string, string> & { lin: string }
const GRADES: { key: GradeKey; titulo: string; codigo: string; campos: { col: string; rotulo: string; inteiro?: boolean }[] }[] = [
  { key: 'salario', titulo: 'Faixa salarial', codigo: 'TPLS002', campos: [
    { col: 'pldsbi', rotulo: 'Salário de' }, { col: 'pldsbf', rotulo: 'Salário até' },
    { col: 'pldpvp', rotulo: '% funcionário' }, { col: 'pldpvd', rotulo: '% dependente' },
  ] },
  { key: 'tempo', titulo: 'Tempo de serviço', codigo: 'TPLS003', campos: [
    { col: 'plttmp', rotulo: 'Tempo', inteiro: true }, { col: 'pltnpp', rotulo: '% funcionário' },
    { col: 'pltnd1', rotulo: 'Nº dep.', inteiro: true }, { col: 'pltnp1', rotulo: '%' },
    { col: 'pltnd2', rotulo: 'Nº dep.', inteiro: true }, { col: 'pltnp2', rotulo: '%' },
    { col: 'pltnd3', rotulo: 'Nº dep.', inteiro: true }, { col: 'pltnp3', rotulo: '%' },
  ] },
  { key: 'idade', titulo: 'Faixa etária', codigo: 'TPLS004', campos: [
    { col: 'pleida', rotulo: 'Idade até', inteiro: true },
    { col: 'plevfu', rotulo: 'Valor funcionário' }, { col: 'plevde', rotulo: 'Valor dependente' },
  ] },
]

function FaixasModal({ plscod, plvdtv, onClose, onSalvo }: {
  plscod: string; plvdtv: string; onClose: () => void
  onSalvo: (qtd: { faixas_salario: number; faixas_tempo: number; faixas_idade: number }) => void
}) {
  const [aba, setAba] = useState<GradeKey>('idade')
  const [dados, setDados] = useState<Record<GradeKey, LinhaFaixa[]> | null>(null)
  const [erro, setErro] = useState('')
  const [salvando, setSalvando] = useState(false)
  const url = `/planos-saude/${encodeURIComponent(plscod)}/validades/${plvdtv}/faixas`

  useEffect(() => {
    api.get(url).then(r => {
      const conv = (lista: any[], g: typeof GRADES[number]) => lista.map(l => {
        const o: LinhaFaixa = { lin: String(l.lin) }
        for (const c of g.campos) o[c.col] = c.inteiro ? String(l[c.col] ?? 0) : moeda(l[c.col])
        return o
      })
      setDados({
        salario: conv(r.data.salario, GRADES[0]), tempo: conv(r.data.tempo, GRADES[1]), idade: conv(r.data.idade, GRADES[2]),
      })
      // abre na grade que já tem linhas
      const comLinhas = GRADES.find(g => r.data[g.key].length > 0)
      if (comLinhas) setAba(comLinhas.key)
    }).catch(e => setErro(e.response?.data?.error ?? 'Erro ao carregar as faixas.'))
  }, [url])

  const grade = GRADES.find(g => g.key === aba)!
  const linhas = dados?.[aba] ?? []
  const setCampo = (i: number, col: string, v: string) =>
    setDados(d => d && { ...d, [aba]: d[aba].map((l, j) => (j === i ? { ...l, [col]: v } : l)) })
  const novaLinha = () => setDados(d => {
    if (!d) return d
    const l: LinhaFaixa = { lin: '' }
    for (const c of grade.campos) l[c.col] = c.inteiro ? '0' : '0,00'
    return { ...d, [aba]: [...d[aba], l] }
  })

  async function salvar() {
    if (!dados) return
    setSalvando(true); setErro('')
    try {
      await api.put(url, dados)
      const r = await api.get(url)
      onSalvo({ faixas_salario: r.data.salario.length, faixas_tempo: r.data.tempo.length, faixas_idade: r.data.idade.length })
    } catch (e: any) {
      setErro(e.response?.data?.error ?? 'Erro ao salvar as faixas.')
    } finally {
      setSalvando(false)
    }
  }

  const th: React.CSSProperties = { textAlign: 'left', padding: '0.4rem 0.5rem', fontSize: '0.72rem' }
  const td: React.CSSProperties = { padding: '0.25rem 0.4rem' }

  return (
    <div className="modal-overlay" style={{ zIndex: 1400 }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth: 820, maxHeight: '92vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header" style={{ gap: '1rem' }}>
          <div style={{ minWidth: 0 }}><h2>Faixas — Plano {plscod}, validade {br(plvdtv) || plvdtv}</h2></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0, marginLeft: 'auto' }}>
            <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'rgba(219,234,254,0.8)' }}>{grade.codigo}</span>
            <button className="modal-fechar" onClick={onClose}>✕</button>
          </div>
        </div>
        <div className="modal-corpo" style={{ overflowY: 'auto' }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: '0.75rem', flexWrap: 'wrap' }}>
            {GRADES.map(g => (
              <button key={g.key} className={`btn btn-sm ${aba === g.key ? 'btn-primary' : 'btn-outline'}`} onClick={() => setAba(g.key)}>
                {g.titulo} ({dados?.[g.key].length ?? 0})
              </button>
            ))}
          </div>
          {!dados ? (!erro && <p style={{ color: '#6b7280' }}>Carregando...</p>) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="table" style={{ width: '100%', margin: 0 }}>
                <thead>
                  <tr>
                    <th style={th}>Linha</th>
                    {grade.campos.map(c => <th key={c.col} style={th}>{c.rotulo}</th>)}
                    <th style={th}></th>
                  </tr>
                </thead>
                <tbody>
                  {linhas.length === 0 && (
                    <tr><td colSpan={grade.campos.length + 2} style={{ ...td, color: '#6b7280', padding: '0.6rem' }}>Nenhuma linha.</td></tr>
                  )}
                  {linhas.map((l, i) => (
                    <tr key={l.lin || `n${i}`}>
                      <td style={{ ...td, color: '#6b7280', fontSize: '0.8rem' }}>{l.lin || 'nova'}</td>
                      {grade.campos.map(c => (
                        <td key={c.col} style={td}>
                          <input inputMode={c.inteiro ? 'numeric' : 'decimal'} value={l[c.col]}
                            style={{ width: c.inteiro ? '6ch' : '10ch', textAlign: 'right' }}
                            onChange={e => setCampo(i, c.col, e.target.value)} />
                        </td>
                      ))}
                      <td style={td}>
                        <button className="btn btn-outline btn-sm" style={{ color: '#b91c1c' }}
                          onClick={() => setDados(d => d && { ...d, [aba]: d[aba].filter((_, j) => j !== i) })}>Excluir</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button className="btn btn-secondary btn-sm" style={{ marginTop: '0.6rem' }} onClick={novaLinha}>+ Nova linha</button>
            </div>
          )}
          {erro && <div style={{ color: '#b91c1c', fontSize: '0.85rem', marginTop: '0.75rem' }}>{erro}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Fechar</button>
          <button className="btn btn-orange" onClick={salvar} disabled={salvando || !dados}>{salvando ? 'Salvando...' : 'Salvar'}</button>
        </div>
      </div>
    </div>
  )
}

function Lista({ lista, onAdicionar, onEditar, onExcluir }: {
  lista: PlanoLista[]
  onAdicionar: () => void
  onEditar: (p: PlanoLista) => void
  onExcluir: (p: PlanoLista) => void
}) {
  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0 }}>Planos cadastrados ({lista.length})</h3>
        <button className="btn btn-primary" onClick={onAdicionar}>+ Adicionar</button>
      </div>

      {lista.length === 0 && <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Nenhum plano de saúde cadastrado.</p>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {lista.map(p => (
          <div key={p.plscod} style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{p.plscod} - {p.plstip || '(sem tipo)'}</div>
              <div style={{ fontSize: '0.78rem', color: '#6b7280', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <span>{p.fornecedor || fmtCnpj(p.plsemp)}</span>
                {p.plsnum && <span>Número: <b>{p.plsnum}</b></span>}
                <span>Última validade: <b>{br(p.ultima_validade) || '—'}</b></span>
                <span>Carteiras: <b>{p.carteiras}</b></span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              <button className="btn btn-outline btn-sm" onClick={() => onEditar(p)}>Editar</button>
              <button className="btn btn-outline btn-sm" style={{ color: '#b91c1c' }} onClick={() => onExcluir(p)}
                disabled={p.carteiras > 0} title={p.carteiras > 0 ? 'Plano usado em carteiras de funcionários' : undefined}>Excluir</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function Formulario({ editando, inicial, onVoltar, onSalvo }: {
  editando: boolean
  inicial: Plano
  onVoltar: () => void
  onSalvo: () => void
}) {
  const [form, setForm] = useState<Plano>(inicial)
  const [buscaForn, setBuscaForn] = useState('')
  const [fornecedores, setFornecedores] = useState<Fornecedor[]>([])
  const [erro, setErro] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [faixasDe, setFaixasDe] = useState<string | null>(null)

  useEffect(() => {
    const t = buscaForn.trim()
    if (t.length < 2) { setFornecedores([]); return }
    const id = setTimeout(() => {
      api.get('/erp/clientes', { params: { busca: t } }).then(r => setFornecedores(r.data)).catch(() => setFornecedores([]))
    }, 300)
    return () => clearTimeout(id)
  }, [buscaForn])

  const setVersao = (i: number, campo: keyof Versao, valor: string) =>
    setForm(f => ({ ...f, versoes: f.versoes.map((v, j) => (j === i ? { ...v, [campo]: valor } : v)) }))

  function removerVersao(i: number) {
    const v = form.versoes[i]
    const faixas = (v.faixas_salario ?? 0) + (v.faixas_tempo ?? 0) + (v.faixas_idade ?? 0)
    if (!v.nova && !window.confirm(`Excluir a validade ${br(v.plvdtv)}?${faixas ? ` As ${faixas} faixa(s) dela também serão excluídas.` : ''} Só é gravado ao salvar.`)) return
    setForm(f => ({ ...f, versoes: f.versoes.filter((_, j) => j !== i) }))
  }

  async function salvar() {
    if (!form.plscod.trim()) { setErro('Informe um código para o plano de saúde!'); return }
    if (!form.plsemp) { setErro('Informe o fornecedor.'); return }
    if (form.versoes.some(v => !v.plvdtv)) { setErro('Informe uma data válida!'); return }
    setSalvando(true); setErro('')
    try {
      await api.put(`/planos-saude/${encodeURIComponent(form.plscod.trim())}`, { ...form, novo: !editando })
      onSalvo()
    } catch (e: any) {
      setErro(e.response?.data?.error ?? 'Erro ao salvar.')
    } finally {
      setSalvando(false)
    }
  }

  const th: React.CSSProperties = { textAlign: 'left', padding: '0.4rem 0.5rem' }
  const td: React.CSSProperties = { padding: '3px 6px' }
  const inpNum: React.CSSProperties = { width: '9ch', textAlign: 'right' }

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0 }}>{editando ? `Plano ${form.plscod}` : 'Novo Plano de Saúde'}</h3>
        <button className="btn btn-outline" onClick={onVoltar}>Voltar</button>
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className="field">
          <label>Código *</label>
          <input value={form.plscod} maxLength={7} disabled={editando} style={{ width: '10ch' }}
            onChange={e => setForm(f => ({ ...f, plscod: e.target.value.toUpperCase() }))} />
        </div>
        <div className="field">
          <label>Número</label>
          <input value={form.plsnum} maxLength={9} style={{ width: '12ch' }} onChange={e => setForm(f => ({ ...f, plsnum: e.target.value }))} />
        </div>
        <div className="field">
          <label>Tipo plano</label>
          <input value={form.plstip} maxLength={40} style={{ width: '42ch' }} onChange={e => setForm(f => ({ ...f, plstip: e.target.value }))} />
        </div>
      </div>

      <div className="field" style={{ marginTop: '0.75rem', position: 'relative', maxWidth: 520 }}>
        <label>Empresa (fornecedor) *</label>
        {form.plsemp ? (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontFamily: 'monospace' }}>{fmtCnpj(form.plsemp)}</span>
            <span style={{ color: '#1b3f7b', fontWeight: 600 }}>{form.fornecedor || '(não encontrado no cadastro)'}</span>
            <button className="btn btn-outline btn-sm" onClick={() => { setForm(f => ({ ...f, plsemp: '', fornecedor: '' })); setBuscaForn('') }}>Trocar</button>
          </div>
        ) : (
          <>
            <input value={buscaForn} placeholder="Nome ou CNPJ do fornecedor" onChange={e => setBuscaForn(e.target.value)} />
            {fornecedores.length > 0 && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, background: '#fff', border: '1px solid #d1d5db', borderRadius: 6, maxHeight: 240, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,.08)' }}>
                {fornecedores.map(c => (
                  <div key={c.cli_mat} style={{ padding: '6px 10px', cursor: 'pointer', fontSize: '0.85rem', borderBottom: '1px solid #f3f4f6' }}
                    onClick={() => { setForm(f => ({ ...f, plsemp: c.cli_mat, fornecedor: c.cli_nom })); setFornecedores([]); setBuscaForn('') }}>
                    <b>{c.cli_nom}</b> <span style={{ color: '#6b7280' }}>{fmtCnpj(c.cli_mat)}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '1.25rem 0 0.4rem' }}>
        <h4 style={{ margin: 0, fontSize: '0.9rem', color: '#1b3f7b' }}>Validades do plano</h4>
        <button className="btn btn-outline btn-sm" onClick={() => setForm(f => ({ ...f, versoes: [versaoVazia(), ...f.versoes] }))}>+ Nova validade</button>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.85rem' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
              <th style={th}>Validade *</th>
              <th style={th}>Início vigência</th>
              <th style={th} title="Valores por faixa salarial e tempo de serviço">R$ funcionário</th>
              <th style={th} title="Valores por faixa salarial e tempo de serviço">R$ dependente</th>
              <th style={th} title="% por faixa etária">% funcionário</th>
              <th style={th} title="% por faixa etária">% dependente</th>
              <th style={th}>Faixas</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {form.versoes.length === 0 && (
              <tr><td colSpan={8} style={{ ...td, color: '#6b7280', padding: '10px 6px' }}>Nenhuma validade cadastrada.</td></tr>
            )}
            {form.versoes.map((v, i) => (
              <tr key={v.nova ? `n${i}` : v.plvdtv} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <td style={td}><input type="date" value={v.plvdtv} disabled={!v.nova} onChange={e => setVersao(i, 'plvdtv', e.target.value)} /></td>
                <td style={td}><input type="date" value={v.plvdiv > '1900-01-01' ? v.plvdiv : ''} onChange={e => setVersao(i, 'plvdiv', e.target.value)} /></td>
                <td style={td}><input style={inpNum} inputMode="decimal" value={v.plvval} onChange={e => setVersao(i, 'plvval', e.target.value)} /></td>
                <td style={td}><input style={inpNum} inputMode="decimal" value={v.plvvld} onChange={e => setVersao(i, 'plvvld', e.target.value)} /></td>
                <td style={td}><input style={inpNum} inputMode="decimal" value={v.plvpfu} onChange={e => setVersao(i, 'plvpfu', e.target.value)} /></td>
                <td style={td}><input style={inpNum} inputMode="decimal" value={v.plvpde} onChange={e => setVersao(i, 'plvpde', e.target.value)} /></td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                  <button className="btn btn-outline btn-sm" disabled={v.nova} onClick={() => setFaixasDe(v.plvdtv)}
                    title={v.nova ? 'Salve o plano antes de lançar as faixas' : 'Faixa salarial / tempo de serviço / faixa etária'}>
                    {v.nova ? 'Faixas' : `Faixas ${v.faixas_salario ?? 0}/${v.faixas_tempo ?? 0}/${v.faixas_idade ?? 0}`}
                  </button>
                </td>
                <td style={td}><button className="btn btn-outline btn-sm" style={{ color: '#b91c1c' }} onClick={() => removerVersao(i)}>Excluir</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {erro && <div style={{ color: '#b91c1c', fontSize: '0.85rem', marginTop: '0.75rem' }}>{erro}</div>}

      <div style={{ display: 'flex', gap: 8, marginTop: '1rem' }}>
        <button className="btn btn-primary" onClick={salvar} disabled={salvando}>
          {salvando ? 'Salvando...' : editando ? 'Salvar alterações' : 'Adicionar'}
        </button>
        <button className="btn btn-outline" onClick={onVoltar}>Cancelar</button>
      </div>

      {faixasDe && (
        <FaixasModal plscod={form.plscod} plvdtv={faixasDe} onClose={() => setFaixasDe(null)}
          onSalvo={qtd => {
            setForm(f => ({ ...f, versoes: f.versoes.map(v => (!v.nova && v.plvdtv === faixasDe ? { ...v, ...qtd } : v)) }))
            setFaixasDe(null)
          }} />
      )}
    </div>
  )
}

export default function CadastroPlanosSaude() {
  const [lista, setLista] = useState<PlanoLista[]>([])
  const [carregado, setCarregado] = useState(false)
  const [atual, setAtual] = useState<Plano | null>(null)
  const [editando, setEditando] = useState(false)
  const [erro, setErro] = useState('')

  async function carregar() {
    const r = await api.get('/planos-saude')
    setLista(r.data)
    setCarregado(true)
  }

  useEffect(() => { carregar().catch(() => setErro('Erro ao carregar os planos.')) }, [])

  function adicionar() {
    setAtual({ plscod: '', plsnum: '', plstip: '', plsemp: '', fornecedor: '', versoes: [versaoVazia()] })
    setEditando(false)
  }

  async function editar(p: PlanoLista) {
    setErro('')
    try {
      const r = await api.get(`/planos-saude/${encodeURIComponent(p.plscod)}`)
      const d = r.data
      setAtual({
        plscod: d.plscod, plsnum: d.plsnum ?? '', plstip: d.plstip ?? '', plsemp: d.plsemp ?? '', fornecedor: d.fornecedor ?? '',
        versoes: d.versoes.map((v: any) => ({
          ...v, plvval: moeda(v.plvval), plvvld: moeda(v.plvvld), plvpfu: moeda(v.plvpfu), plvpde: moeda(v.plvpde),
        })),
      })
      setEditando(true)
    } catch (e: any) {
      setErro(e.response?.data?.error ?? 'Erro ao abrir o plano.')
    }
  }

  async function excluir(p: PlanoLista) {
    if (!window.confirm(`Excluir o plano "${p.plscod} - ${p.plstip}" com todas as validades e faixas?`)) return
    try {
      await api.delete(`/planos-saude/${encodeURIComponent(p.plscod)}`)
      await carregar()
    } catch (e: any) {
      setErro(e.response?.data?.error ?? 'Erro ao excluir.')
    }
  }

  async function salvo() {
    await carregar()
    setAtual(null)
  }

  return (
    <div style={{ maxWidth: 960 }}>
      <h2 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
        Cadastro de Plano de Saúde
        <span style={{ color: '#9ca3af', fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: 400 }} title="Tabelas frota.pls001 / pls002">TPLS001</span>
      </h2>
      {erro && <div style={{ color: '#b91c1c', fontSize: '0.85rem', marginBottom: '0.75rem' }}>{erro}</div>}

      {!carregado ? null : atual ? (
        <Formulario key={atual.plscod || 'novo'} editando={editando} inicial={atual} onVoltar={() => setAtual(null)} onSalvo={salvo} />
      ) : (
        <Lista lista={lista} onAdicionar={adicionar} onEditar={editar} onExcluir={excluir} />
      )}
    </div>
  )
}
